import React from 'react';
import { RefreshCw, Search } from 'lucide-react';

import type { PricePresentation } from '../../market/pricePresentation';
import { CustomSelect } from '../CustomElements';
import {
  filterPositionSuggestions,
  getContractDisplaySymbol,
  isFutureContract,
  type ContractSelection,
} from './contractSelection';

const SEC_TYPE_OPTIONS = ['STK', 'FUT', 'OPT', 'CFD', 'CASH'].map((value) => ({ value, label: value }));

interface ContractPickerProps {
  symbol: string;
  secType: string;
  isEditing: boolean;
  positionSuggestions: ContractSelection[];
  lookupResults: ContractSelection[];
  lookupLoading: boolean;
  showDropdown: boolean;
  selectedContract: ContractSelection | null;
  marketPrice: number | null;
  fetchingPrice: boolean;
  pricePresentation: PricePresentation;
  onSymbolChange: (symbol: string) => void;
  onSymbolFocus: () => void;
  onSymbolBlur: () => void;
  onSecTypeChange: (secType: string) => void;
  onLookup: () => void;
  onSelectContract: (contract: ContractSelection) => void;
  onRefreshQuote: () => void;
}

const ContractPicker: React.FC<ContractPickerProps> = ({
  symbol,
  secType,
  isEditing,
  positionSuggestions,
  lookupResults,
  lookupLoading,
  showDropdown,
  selectedContract,
  marketPrice,
  fetchingPrice,
  pricePresentation,
  onSymbolChange,
  onSymbolFocus,
  onSymbolBlur,
  onSecTypeChange,
  onLookup,
  onSelectContract,
  onRefreshQuote,
}) => {
  const suggestions = filterPositionSuggestions(positionSuggestions, symbol);
  const hasDropdown = showDropdown && (suggestions.length > 0 || lookupResults.length > 0 || lookupLoading);
  const hasLatestPriceRow = marketPrice !== null || fetchingPrice;

  return (
    <div className="flex gap-3">
      <div className="flex-[2] relative">
        <label className="block text-[10px] font-bold text-muted uppercase mb-1 tracking-wider opacity-70">Symbol</label>
        <div className="relative">
          <input
            type="text"
            value={symbol}
            onFocus={onSymbolFocus}
            onChange={(event) => onSymbolChange(event.target.value.toUpperCase())}
            disabled={isEditing}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onLookup();
              }
            }}
            onBlur={onSymbolBlur}
            placeholder={positionSuggestions[0] ? getContractDisplaySymbol(positionSuggestions[0]) : 'SPY'}
            className={`w-full bg-white/[0.03] border border-white/10 px-3 pr-9 py-2 text-foreground placeholder-muted/30 focus:outline-none focus:border-neon/50 focus:bg-white/[0.05] transition h-9 text-xs ${hasDropdown ? 'rounded-t-lg border-b-0' : 'rounded-lg'}`}
            required
          />
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onLookup}
            disabled={isEditing || lookupLoading || !symbol}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-muted hover:text-neon hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition flex items-center justify-center"
            title="Lookup qualified IB contracts"
          >
            <Search className={`w-3.5 h-3.5 ${lookupLoading ? 'animate-pulse' : ''}`} />
          </button>
        </div>
        {hasLatestPriceRow && (
          <div className="mt-1 flex h-5 items-center justify-between rounded-md border border-white/5 bg-white/[0.025] px-2 text-[10px]">
            <span className="font-bold uppercase tracking-wider text-muted/60">Latest</span>
            <button
              type="button"
              onClick={onRefreshQuote}
              disabled={fetchingPrice}
              title={isFutureContract(selectedContract) ? 'Refresh futures live snapshot' : 'Refresh latest price'}
              className={`flex items-center gap-1 font-mono font-bold transition hover:text-neon disabled:opacity-50 ${pricePresentation.priceClassName}`}
            >
              <RefreshCw className={`h-2.5 w-2.5 ${fetchingPrice ? 'animate-spin' : ''}`} />
              {marketPrice !== null ? `$${marketPrice.toFixed(2)}` : 'Loading'}
              {marketPrice !== null && (
                <span className={`ml-1 rounded border px-1 text-[8px] uppercase ${pricePresentation.badgeClassName}`}>
                  {pricePresentation.status}
                </span>
              )}
            </button>
          </div>
        )}
        {hasDropdown && (
          <div className={`absolute z-[60] left-0 ${hasLatestPriceRow ? 'top-[calc(100%+1.25rem)]' : 'top-[calc(100%-1px)]'} min-w-full w-[min(32rem,calc(100vw-2rem))] bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl overflow-hidden backdrop-blur-xl animate-in fade-in slide-in-from-top-1 duration-200`}>
            <div className="py-1 max-h-64 overflow-y-auto ux-scrollbar">
              {suggestions.length > 0 && (
                <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted/60">Open positions</div>
              )}
              {suggestions.map((item) => (
                <button
                  key={`position-${item.symbol}-${item.sec_type}-${item.con_id || item.local_symbol || ''}`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onSelectContract(item)}
                  className="w-full px-4 py-2.5 text-left text-xs font-bold text-white/70 hover:text-neon hover:bg-white/5 transition-colors border-b border-white/[0.03] last:border-0 flex items-center justify-between gap-3"
                >
                  <span className="truncate">{getContractDisplaySymbol(item)}</span>
                  <span className="text-[10px] text-muted font-mono whitespace-nowrap">{item.sec_type}{item.last_trade_date_or_contract_month ? ` ${item.last_trade_date_or_contract_month}` : ''}</span>
                </button>
              ))}
              {lookupLoading && (
                <div className="px-4 py-2.5 text-xs text-muted flex items-center gap-2 border-t border-white/[0.04]">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Looking up contracts...
                </div>
              )}
              {lookupResults.length > 0 && (
                <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted/60 border-t border-white/[0.04]">Lookup results</div>
              )}
              {lookupResults.map((item) => (
                <button
                  key={`lookup-${item.con_id || item.local_symbol}-${item.exchange}`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onSelectContract(item)}
                  className="w-full px-4 py-2.5 text-left text-xs text-white/75 hover:bg-white/5 hover:text-neon transition border-b border-white/[0.04] last:border-0 flex items-center justify-between gap-3"
                >
                  <span className="min-w-0">
                    <span className="block font-bold truncate">{item.local_symbol || item.symbol}</span>
                    {item.long_name && <span className="block text-[10px] text-muted truncate font-normal">{item.long_name}</span>}
                  </span>
                  <span className="text-[10px] text-muted font-mono whitespace-nowrap">
                    {item.sec_type} {item.last_trade_date_or_contract_month || ''} {item.exchange || ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 relative">
        <label className="block text-[10px] font-bold text-muted uppercase mb-1 tracking-wider opacity-70">Sec Type</label>
        <CustomSelect
          value={secType}
          onChange={onSecTypeChange}
          options={SEC_TYPE_OPTIONS}
          ariaLabel="Security type"
          disabled={isEditing}
        />
      </div>
    </div>
  );
};

export default ContractPicker;
